import { AMOUNT_TOO_LOW, UNKNOWN_ERROR } from '../constants/swapErrors'
import { PlugToken } from '../interfaces/common'
import {
  SwapProvider,
  SwapProviderQuote,
  SwapProviderQuoteFetchError,
} from '../interfaces/swapInterface'
import { Wallet } from '../providers/WalletProvider'
import { getWalletAddress } from '../utils/getWalletAddress'
import { sendToken } from '../utils/sendToken'
import { decimalsToE8s, e8sToDecimals } from '../utils/validationHelper'
import LedgerScannerClient from './LedgerScannerClient'

interface EstimatedAmountApiResponse {
  estimated_amount: string
  err?: {
    details: string
  }
}

interface ExchangeApiSuccessResponse {
  id: string
  status:
    | 'waiting'
    | 'confirming'
    | 'exchanging'
    | 'sending'
    | 'verifying'
    | 'finished'
    | 'failed'
    | 'refunded'
    | 'expired'
  rate: 'floating' | 'fixed'
  deposit: {
    symbol: string
    network: string
    amount: number
    expected_amount: number
    address: string
    extra_id: string | null
    tx_hash: string | null
    address_explorer_url: string | null
    tx_explorer_url: string | null
  }
  withdrawal: {
    symbol: string
    network: string
    amount: number
    expected_amount: number
    address: string
    extra_id: string | null
    tx_hash: string | null
    address_explorer_url: string | null
    tx_explorer_url: string | null
  }
  refund_address: string | null
  refund_extra_id: string | null
  created_at: string
  expires_at: string | null
}

interface ExchangeApiErrorResponse {
  err: {
    kind: string
    details: string
  }
}

type ExchangeApiResponse = ExchangeApiSuccessResponse | ExchangeApiErrorResponse

class Stealthex {
  async getQuote(
    amount: bigint,
    fromToken: PlugToken,
    toToken: PlugToken
  ): Promise<SwapProviderQuote | SwapProviderQuoteFetchError> {
    try {
      const response: EstimatedAmountApiResponse = await LedgerScannerClient.proxy(
        'stealthex',
        'v4/rates/estimated-amount',
        { POST: this.getExchangeBody(amount, fromToken, toToken) }
      )

      if (response?.estimated_amount) {
        return {
          price: Number(response.estimated_amount) * 10 ** toToken.decimals,
          priceHuman: Number(response.estimated_amount),
          provider: SwapProvider.STEALTHEX,
        }
      } else {
        let error = response.err?.details || UNKNOWN_ERROR
        if (error === 'Amount is out of range') {
          error = AMOUNT_TOO_LOW
        }

        return { error, provider: SwapProvider.STEALTHEX }
      }
    } catch (e) {
      return { error: UNKNOWN_ERROR, provider: SwapProvider.STEALTHEX }
    }
  }

  async swap(amount: bigint, fromToken: PlugToken, toToken: PlugToken, wallet: Wallet) {
    const response: ExchangeApiResponse = await LedgerScannerClient.proxy(
      'stealthex',
      'v4/exchanges',
      {
        POST: {
          ...this.getExchangeBody(amount, fromToken, toToken),
          address: getWalletAddress(toToken, wallet, { useIcpAccountId: true }),
        },
      }
    )

    if ('err' in response) {
      throw new Error(response.err.details)
    } else {
      const { deposit, withdrawal } = response
      const depositAmount = BigInt(
        decimalsToE8s(deposit.expected_amount.toString(), fromToken.decimals)
      )

      await sendToken(fromToken, deposit.address, depositAmount, wallet)

      return withdrawal.expected_amount
    }
  }

  private getExchangeBody(amount: bigint, fromToken: PlugToken, toToken: PlugToken) {
    return {
      route: {
        from: {
          symbol: fromToken.additionalData.stealthex_symbol,
          network: fromToken.additionalData.stealthex_network,
        },
        to: {
          symbol: toToken.additionalData.stealthex_symbol,
          network: toToken.additionalData.stealthex_network,
        },
      },
      estimation: 'direct',
      rate: 'floating',
      amount: e8sToDecimals(amount, fromToken.decimals),
    }
  }
}

export default Stealthex
